// Scheduling only: when to probe the session. What a probe does lives with the caller.

export const HEARTBEAT_INTERVAL_MS = 60000;

export interface HeartbeatScheduler {
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
}

export interface HeartbeatOptions {
  /** one session check; the loop never runs two at once */
  probe: () => Promise<void>;
  isVisible: () => boolean;
  scheduler: HeartbeatScheduler;
  intervalMs?: number;
}

export interface Heartbeat {
  /** call on every visibilitychange: probes at once on becoming visible, pauses while hidden */
  visibilityChanged: () => void;
  stop: () => void;
}

export function startHeartbeat(options: HeartbeatOptions): Heartbeat {
  const { probe, isVisible, scheduler } = options;
  const intervalMs = options.intervalMs || HEARTBEAT_INTERVAL_MS;
  let stopped = false;
  let inFlight = false;
  let timer: unknown = null;

  function disarm(): void {
    if (timer !== null) scheduler.clearTimeout(timer);
    timer = null;
  }

  function arm(): void {
    disarm();
    if (!stopped && isVisible()) timer = scheduler.setTimeout(beat, intervalMs);
  }

  function settle(): void {
    inFlight = false;
    arm();
  }

  function beat(): void {
    timer = null;
    if (stopped || inFlight || !isVisible()) return;
    inFlight = true;
    let pending: Promise<void>;
    try {
      pending = probe();
    } catch (e) {
      settle();
      return;
    }
    pending.then(settle, settle);
  }

  arm();

  return {
    visibilityChanged() {
      if (stopped) return;
      if (!isVisible()) {
        disarm();
        return;
      }
      // a probe in flight re-arms when it settles
      if (inFlight) return;
      disarm();
      beat();
    },
    stop() {
      stopped = true;
      disarm();
    },
  };
}
